import React, { useState, useEffect } from 'react';
import { View, ScrollView, Alert } from 'react-native';
import { styles } from './CompanyLandingScreen.styles';
import Button from 'src/components/Button/Button';
import { useLinkTo } from '@react-navigation/native';
import { URLS } from 'src/constants/urls';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/store/store';
import {
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
} from 'src/features/company/api/companyApi';
import { useLazyGetProducerPhoneQuery } from 'src/features/user/api/userApi';
import { resetDirtyFlag } from 'src/features/company/slice/companySlice';
import { TABS_INDEX } from 'src/constants/tabs';
import { FLOW_STEP } from 'src/constants/company';
import { setActiveTab, setFilledTabsIndex, setShowGlobalLoader } from 'src/slices/UiSlice';
import { toggleToastState } from 'src/helpers/toastHelper';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents';
import { useRegisterUserMutation } from 'src/features/authentication/api/authenticationApi';
import { setUserToken } from 'src/features/user/slice/userSlice';
import CompanyDetails from 'src/features/company/components/CompanyDetails';
//import ConsentText from 'src/features/company/components/ConsentText';
import ErrorToast from 'src/features/company/components/ErrorToast';
import Modals from 'src/features/company/components/Modals';
import CompanyBannerV2 from 'src/features/company/components/CompanyBanner/CompanyBannerV2';
import { normalizeCompany } from 'src/features/company/helpers/companyHelpers';
import { useTourAutoStart } from 'src/features/tour/hooks/useTourAutoStart';
import { getCompanyLandingTour } from 'src/features/tour/tourSteps';
import TourWrapper from 'src/components/TourWrapper/TourWrapper';
// import growthbook from 'src/config/GrowthBook';
import Loader from 'src/components/loadingTruck/loadingTruck';

const CompanyLandingScreen: React.FC = () => {
  const linkTo = useLinkTo();
  const dispatch = useDispatch();

  const [saveComanyErrorToast, setSaveCompanyErrorToast] = useState(false);
  const [createUser /*{ error: userCreateError }*/] = useRegisterUserMutation();
  const [leadModalVisible, setLeadModalVisible] = useState(false);
  const [secondUserModalVisible, setSecondUserModalVisible] = useState(false);
  const [dataFilledByModalVisible, setDataFilledByModalVisible] = useState(false);
  const [isInitializationComplete, setIsInitializationComplete] = useState(false);

  const company = useSelector((state: RootState) => state.company.company);
  const vehicles = useSelector((state: RootState) => state.vehicles.vehicles);
  const isDirty = useSelector((state: RootState) => state.company.isDirty);
  const accountType = useSelector((state: RootState) => state.UI.accountType);
  const tpJwtToken = useSelector((state: RootState) => state.UI.tpJwtToken);
  const accountSource = useSelector((state: RootState) => state.UI.accountSource);
  const user = useSelector((state: RootState) => state.users.currentUser);

  const [createCompany, { isLoading: isCreating }] = useCreateCompanyMutation();
  const [updateCompany, { isLoading: isUpdating }] = useUpdateCompanyMutation();
  const [getProducerPhoneLazy] = useLazyGetProducerPhoneQuery();

  const isConfirmDisabled =
    !company?.dateBusinessStarted ||
    !company?.companyName ||
    !(company.garagingAddress.street.length > 0);

  // Auto-start tour on first visit (after user and company are created)
  useTourAutoStart({
    tourKey: 'companyLanding',
    driverSteps: getCompanyLandingTour(),
    enabled: !!company && !isCreating && !isUpdating && isInitializationComplete,
  });

  useEffect(() => {
    // Track the event when the Company Summary Page is landed
    if (company?.dotNumber) {
      trackGTMEvent(
        'company-summary-page-landed',
        {
          screenName: 'CompanyLandingScreen',
          userEvent: 'User landed on company summary screen',
          companyId: company?.id,
          bannerVariant: 'v2',
        },
        accountType,
      );
    }
  }, []);

  const handleGetAQuote = async () => {
    try {
      dispatch(setShowGlobalLoader(true)); // Set immediately
      trackGetAQuoteButtonPress();

      // Update company if there are any changes
      if (company?.id && isDirty) {
        try {
          await updateCompany({ id: company.id, updateCompanyData: company }).unwrap();
          dispatch(resetDirtyFlag());
        } catch {
          setSaveCompanyErrorToast(true);
          toggleToastState(setSaveCompanyErrorToast);
          return; // Don't navigate if update fails
        }
      }

      // Check if we should show data filled by modal
      const shouldShowDataFilledByModal =
        company?.id &&
        company.id > 0 &&
        company.dataFilledBy === null &&
        vehicles.length < 1 &&
        company.flowStep === FLOW_STEP.SALES_FLOW &&
        accountType !== 'producer';

      if (shouldShowDataFilledByModal) {
        setDataFilledByModalVisible(true);
      }

      // Check if we should show lead modal
      if (company?.leadScore === 'SENIOR_BROKER_TRACK') {
        setLeadModalVisible(true);
      }

      if (!shouldShowDataFilledByModal) {
        // Navigate to vehicles tab
        navigateToVehiclesTab();
      }
    } catch (error) {
      handleError('Failed to save company data. Please try again.', error);
    } finally {
      dispatch(setShowGlobalLoader(false)); // Always unset when done
    }
  };

  const trackGetAQuoteButtonPress = () => {
    trackGTMEvent(
      'company-summary-screen-confirm-pressed',
      {
        buttonTitle: 'Get A Quote',
        bannerVariant: 'v2',
        screenName: 'CompanyLandingScreen',
      },
      accountType,
    );
  };

  const handleCreateUser = async () => {
    if (!user) {
      console.error('User data is not available');
      return false;
    }

    try {
      const userData = await createUser({
        newUser: user,
        token: tpJwtToken || '',
        dotNumber: company?.dotNumber,
        accountSource,
      }).unwrap();
      dispatch(setUserToken(userData.token || ''));

      if (!userData.user.enabled) {
        setSecondUserModalVisible(true);
        return false;
      }

      return true; // User created successfully
    } catch (error) {
      console.error('User creation error:', error);
      setSaveCompanyErrorToast(true);
      toggleToastState(setSaveCompanyErrorToast);
      return false;
    }
  };

  // Create user and company on page load if not already created
  useEffect(() => {
    const initializeUserAndCompany = async () => {
      let userReady = false;
      let companyReady = false;

      // If user doesn't exist, create user first
      if (!user?.id && user && tpJwtToken) {
        const userCreated = await handleCreateUser();
        if (!userCreated) {
          return; // Stop if user creation failed or user is not enabled
        }
        userReady = true;
      } else if (user?.id) {
        // User already exists
        userReady = true;
      }

      // Check if company data is valid before creating
      const isCompanyDataValid =
        company?.dateBusinessStarted &&
        company?.companyName &&
        company.garagingAddress?.street?.length > 0;

      // If company doesn't exist and data is valid, create company
      if (!company?.id && isCompanyDataValid) {
        await handleNewCompany();
        companyReady = true;
      } else if (company?.id) {
        // Company already exists
        companyReady = true;
      }

      // Mark initialization as complete when both user and company are ready
      if (userReady && companyReady) {
        setIsInitializationComplete(true);
      }
    };

    initializeUserAndCompany();
  }, []);

  const handleNewCompany = async () => {
    const newCompanyPayload = company || {}; // Fallback to an empty object if company is null

    try {
      await createCompany(newCompanyPayload).unwrap();
      await getProducerPhoneLazy().unwrap(); // Fetch producer phone number
      dispatch(resetDirtyFlag());
    } catch (error) {
      setSaveCompanyErrorToast(true);
      toggleToastState(setSaveCompanyErrorToast);
      console.error('Company creation error:', error);
    }
  };

  const navigateToVehiclesTab = () => {
    dispatch(setActiveTab({ activeTab: TABS_INDEX.VEHICLE }));
    dispatch(setFilledTabsIndex({ filledTabsIndex: TABS_INDEX.VEHICLE }));
    linkTo(URLS.VEHICLE.START);
  };

  const handleError = (message: string, error: unknown) => {
    Alert.alert('Error', message);
    setSaveCompanyErrorToast(true);
    toggleToastState(setSaveCompanyErrorToast);
    console.error(message, error);
  };

  const handleEditPressed = () => {
    linkTo(URLS.COMPANY.DETAILS);

    trackGTMEvent(
      'company-summary-screen-left-for-edit',
      {
        buttonTitle: 'Edit',
        screenName: 'CompanyLandingScreen',
      },
      accountType,
    );
  };

  if (isCreating || isUpdating) {
    return <Loader text="Loading..." />;
  }

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <CompanyBannerV2 />
      <CompanyDetails company={normalizeCompany(company)} onEdit={handleEditPressed} />
      <ErrorToast saveCompanyErrorToast={saveComanyErrorToast} />
      <View style={styles.buttonContainer}>
        <View style={styles.buttonWrapper2}>
          <TourWrapper tourId="get-quote-btn">
            <Button
              type="primary"
              colorVariant={isConfirmDisabled ? 'disabled' : 'default'}
              testID="landing-page-get-quote-btn"
              title="Get A Quote"
              onPress={handleGetAQuote}
              disabled={isConfirmDisabled}
            />
          </TourWrapper>
        </View>
      </View>
      {/*<ConsentText />*/}
      <Modals
        secondUserModalVisible={secondUserModalVisible}
        leadModalVisible={leadModalVisible}
        setLeadModalVisible={setLeadModalVisible}
        dataFilledByModalVisible={dataFilledByModalVisible}
        setDataFilledByModalVisible={setDataFilledByModalVisible}
        navigateToVehiclesTab={navigateToVehiclesTab}
        dotNumber={company?.dotNumber || 'N/A'}
        company={company}
      />
    </ScrollView>
  );
};

export default CompanyLandingScreen;
